/**
 * Canonical service registry for UI metadata (Phase 0 — PBI 977).
 *
 * Every component that needs service labels, colors, icons, or action-type mapping
 * MUST read from this module instead of branching on ServiceType / ScheduledActionType.
 *
 * @see .cursor/rules/service-type-badge.mdc
 */

import type { ComponentType } from 'react';
import type { Icon } from '@tabler/icons-react';
import { IconMail, IconFlower, IconMailbox, IconGift } from '@tabler/icons-react';
import { ServiceType, ScheduledActionType } from '../types';
import { APP_ROUTES, PUBLIC_ROUTES } from '../constants/routes';
import {
  CatalogShopServiceKeys,
  type CatalogShopServiceKey,
} from '../constants/catalogShopServiceKeys';
import { FeatureFlagKeys } from '../constants/featureFlagKeys';
import { serviceColors } from './theme';

/** Placeholder for registry-driven scheduled-item actions (populated in PBI 981). */
export interface ScheduledItemActionDescriptor {
  id: string;
  label: string;
}

/**
 * Catalog shop page metadata for public + auth browse surfaces (PBI #1119).
 * New catalog services must add shopPage and implement both bucket contracts.
 */
export interface CatalogShopPageConfig {
  /** Public marketing route (e.g. /cards). */
  publicPath: string;
  /** Authenticated shop route (e.g. /templates). */
  authPath: string;
  /** Key on landingContent for public hero title/subtitle. */
  publicLandingContentKey: 'cardsPage' | 'flowersPage' | 'mailedCardsPage' | 'giftsPage';
  /** API service query value for occasion discovery. */
  apiServiceKey: CatalogShopServiceKey;
  /** Source file for the public shop page (AST allowlist). */
  publicPageFile: string;
  /** Source file for the auth shop page (AST allowlist). */
  authPageFile: string;
}

/**
 * UI configuration for a schedulable service.
 */
export interface ServiceConfig {
  type: ServiceType;
  actionType: ScheduledActionType;
  icon: Icon;
  label: string;
  color: string;
  featureFlagKey: string | null;
  subscriptionFeature: string | null;
  scheduledItemLabel: string;
  scheduleButtonLabel: string;
  emptyStateMessage: string;
  orderModalComponent: ComponentType<unknown> | null;
  orderDetailsComponent: ComponentType<unknown> | null;
  pendingActions: ScheduledItemActionDescriptor[];
  sentActions: ScheduledItemActionDescriptor[];
  /** Present when the service has public + auth catalog shop pages. */
  shopPage?: CatalogShopPageConfig;
}

const eCardConfig: ServiceConfig = {
  type: ServiceType.ECard,
  actionType: ScheduledActionType.Email,
  icon: IconMail,
  label: 'E-Card',
  color: serviceColors.eCard,
  featureFlagKey: null,
  subscriptionFeature: null,
  scheduledItemLabel: 'E-Card',
  scheduleButtonLabel: 'Schedule card',
  emptyStateMessage: 'No cards scheduled for this event.',
  orderModalComponent: null,
  orderDetailsComponent: null,
  pendingActions: [],
  sentActions: [],
  shopPage: {
    publicPath: PUBLIC_ROUTES.cards,
    authPath: APP_ROUTES.eCards,
    publicLandingContentKey: 'cardsPage',
    apiServiceKey: CatalogShopServiceKeys.ECard,
    publicPageFile: 'src/pages/CardsPage.tsx',
    authPageFile: 'src/pages/TemplatesPage.tsx',
  },
};

const flowersConfig: ServiceConfig = {
  type: ServiceType.Flowers,
  actionType: ScheduledActionType.FlowerOrder,
  icon: IconFlower,
  label: 'Flowers',
  color: serviceColors.flowers,
  featureFlagKey: 'FlowerCatalog',
  subscriptionFeature: null,
  scheduledItemLabel: 'Flowers',
  scheduleButtonLabel: 'Order flowers',
  emptyStateMessage: 'No flower orders for this event.',
  orderModalComponent: null,
  orderDetailsComponent: null,
  pendingActions: [],
  sentActions: [],
  shopPage: {
    publicPath: PUBLIC_ROUTES.flowers,
    authPath: APP_ROUTES.flowers,
    publicLandingContentKey: 'flowersPage',
    apiServiceKey: CatalogShopServiceKeys.Flowers,
    publicPageFile: 'src/pages/PublicFlowersPage.tsx',
    authPageFile: 'src/pages/FlowersPage.tsx',
  },
};

/** Mailed Card pipeline (PBI 978 backend; modal wiring lands in PBI 981). */
const mailedCardConfig: ServiceConfig = {
  type: ServiceType.MailedCard,
  actionType: ScheduledActionType.MailedCardOrder,
  icon: IconMailbox,
  label: 'Mailed Card',
  color: serviceColors.mailedCard,
  featureFlagKey: 'MailedCardCatalog',
  subscriptionFeature: null,
  scheduledItemLabel: 'Mailed Card',
  scheduleButtonLabel: 'Send mailed card',
  emptyStateMessage: 'No mailed cards for this event.',
  // Modals are imported at use sites (ContactActionModals, shop pages) — not here,
  // so marketingTracking → shopNav → serviceRegistry does not eager-load ContactForm.
  orderModalComponent: null,
  orderDetailsComponent: null,
  pendingActions: [
    { id: 'viewMailedCardOrder', label: 'View Order' },
    { id: 'editMailedCardOrder', label: 'Edit Order' },
    { id: 'cancelMailedCardOrder', label: 'Cancel Order' },
  ],
  sentActions: [
    { id: 'viewMailedCardOrder', label: 'View Order' },
  ],
  shopPage: {
    publicPath: PUBLIC_ROUTES.mailedCards,
    authPath: APP_ROUTES.mailedCards,
    publicLandingContentKey: 'mailedCardsPage',
    apiServiceKey: CatalogShopServiceKeys.MailedCards,
    publicPageFile: 'src/pages/PublicMailedCardsPage.tsx',
    authPageFile: 'src/pages/MailedCardsPage.tsx',
  },
};

const giftsConfig: ServiceConfig = {
  type: ServiceType.Gift,
  actionType: ScheduledActionType.GiftOrder,
  icon: IconGift,
  label: 'Gift',
  color: serviceColors.gifts,
  featureFlagKey: FeatureFlagKeys.GiftCatalog,
  subscriptionFeature: null,
  scheduledItemLabel: 'Gift',
  scheduleButtonLabel: 'Schedule gift',
  emptyStateMessage: 'No gifts scheduled for this event.',
  orderModalComponent: null,
  orderDetailsComponent: null,
  pendingActions: [
    { id: 'cancelGiftOrder', label: 'Cancel Order' },
  ],
  sentActions: [],
  shopPage: {
    publicPath: PUBLIC_ROUTES.gifts,
    authPath: PUBLIC_ROUTES.gifts,
    publicLandingContentKey: 'giftsPage',
    apiServiceKey: CatalogShopServiceKeys.Gifts,
    publicPageFile: 'src/pages/PublicGiftsPage.tsx',
    // Public-only shop until an in-app gift browse page ships (auth contract skipped in AST).
    authPageFile: 'src/pages/PublicGiftsPage.tsx',
  },
};

/** All registered services keyed by <see cref="ServiceType"/>. */
export const serviceRegistry: Record<ServiceType, ServiceConfig> = {
  [ServiceType.ECard]: eCardConfig,
  [ServiceType.Flowers]: flowersConfig,
  [ServiceType.MailedCard]: mailedCardConfig,
  [ServiceType.Gift]: giftsConfig,
};

/** Lookup by enrollment service type. */
export function getServiceConfigByType(serviceType: ServiceType): ServiceConfig {
  return serviceRegistry[serviceType];
}

/**
 * Services that expose public + auth catalog shop pages (PBI #1119).
 */
export function getCatalogShopServices(): ServiceConfig[] {
  return Object.values(serviceRegistry).filter(
    (config): config is ServiceConfig & { shopPage: CatalogShopPageConfig } =>
      config.shopPage != null,
  );
}

/** True when the action type is the Flowers pipeline (canonical branching helper). */
export function isFlowerActionType(actionType: ScheduledActionType): boolean {
  return actionType === ScheduledActionType.FlowerOrder;
}

/** True when the action type is the Mailed Card pipeline (canonical branching helper). */
export function isMailedCardActionType(actionType: ScheduledActionType): boolean {
  return actionType === ScheduledActionType.MailedCardOrder;
}

/** True when the action type is the Gift pipeline (PBI #1528). */
export function isGiftActionType(actionType: ScheduledActionType): boolean {
  return actionType === ScheduledActionType.GiftOrder;
}

/** Lookup by scheduled item action type. */
export function getServiceConfigByActionType(actionType: ScheduledActionType): ServiceConfig {
  const match = Object.values(serviceRegistry).find((config) => config.actionType === actionType);
  return match ?? eCardConfig;
}

/**
 * @deprecated Use getServiceConfigByType — kept for incremental migration.
 */
export const SERVICE_CONFIG_BY_TYPE: Record<ServiceType, Pick<ServiceConfig, 'icon' | 'label' | 'color'>> = {
  [ServiceType.ECard]: eCardConfig,
  [ServiceType.Flowers]: flowersConfig,
  [ServiceType.MailedCard]: mailedCardConfig,
  [ServiceType.Gift]: giftsConfig,
};

/**
 * @deprecated Use getServiceConfigByActionType — kept for incremental migration.
 */
export const SERVICE_CONFIG_BY_ACTION: Record<string, Pick<ServiceConfig, 'icon' | 'label' | 'color'>> = {
  [ScheduledActionType.Email]: eCardConfig,
  [ScheduledActionType.FlowerOrder]: flowersConfig,
  [ScheduledActionType.MailedCardOrder]: mailedCardConfig,
  [ScheduledActionType.GiftOrder]: giftsConfig,
};
